import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { useContext, useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import type {
  CreateUserRequest,
  DeleteUserRequest,
  EditUserRequest,
  EditUserResponse,
} from "../../api/user";
import DeleteDialog from "../../components/common/DeleteDialog/DeleteDialog";
import { RoleNameDisplay } from "../../components/common/FriendlyDisplay/RoleNameDisplay";
import ResultsPage from "../../components/common/ResultsPage/ResultsPage";
import { UseShowAlert, useAlert } from "../../context/AlertContext";
import {
  useCreateUser,
  useDeleteUser,
  useEditUser,
  useUsers,
} from "../../hooks/useUser";
import ErrorMessage from "../../types/feedback/ErrorMessage";
import User from "../../types/user/User";
import UserForm, { UserAction, UserFormValues } from "./UserForm";
import { useTranslation } from "react-i18next";
import { LockReset } from "@mui/icons-material";
import { useOpenable } from "../../hooks/useOpenable";
import { AdminResetPasswordDialog } from "./AdminResetPasswordDialog";
import { Tooltip } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { isAdmin } from "../../helpers/authHelpers";

export const createUser = async (
  data: UserFormValues,
  createUserMutation: UseMutationResult<
    User | undefined,
    unknown,
    CreateUserRequest,
    unknown
  >,
  showAlert: UseShowAlert,
  setIsFormOpen: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  try {
    const request: CreateUserRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email!,
      roleId: data.role?.roleId ?? "",
      password: data.password!,
    };
    const user = await createUserMutation.mutateAsync(request);
    if (!user) {
      throw new Error("Failed to create user.");
    }
    showAlert("User created successfully.", "success");
    setIsFormOpen(false);
  } catch (error: unknown) {
    if (error instanceof Error) {
      showAlert(error.message, "error");
    } else {
      showAlert(ErrorMessage.UNKNOWN, "error");
    }
  }
};

export const editUser = async (
  data: UserFormValues,
  selectedRowId: string | null,
  editUserMutation: UseMutationResult<
    EditUserResponse,
    unknown,
    EditUserRequest,
    unknown
  >,
  showAlert: UseShowAlert,
  setIsFormOpen: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  try {
    const request: EditUserRequest = {
      userId: selectedRowId!,
      firstName: data.firstName,
      lastName: data.lastName,
      roleId: data.role?.roleId ?? "",
    };
    const { user } = await editUserMutation.mutateAsync(request);
    if (!user) {
      throw new Error("Failed to edit user.");
    }
    showAlert("User edited successfully.", "success");
    setIsFormOpen(false);
  } catch (error: unknown) {
    if (error instanceof Error) {
      showAlert(error.message, "error");
    } else {
      showAlert(ErrorMessage.UNKNOWN, "error");
    }
  }
};

export const deleteUser = async (
  deleteUserMutation: UseMutationResult<
    void,
    unknown,
    DeleteUserRequest,
    unknown
  >,
  selectedRowId: string | null,
  showAlert: UseShowAlert,
  users: User[],
): Promise<void> => {
  try {
    await deleteUserMutation.mutateAsync({
      userId: selectedRowId!,
      username: users.find((user) => user.userId === selectedRowId!)?.email,
    });
    showAlert("User deleted successfully.", "success");
  } catch (error: unknown) {
    if (error instanceof Error) {
      showAlert(error.message, "error");
    } else {
      showAlert(ErrorMessage.UNKNOWN, "error");
    }
  }
};

export default function UserList() {
  const { t } = useTranslation("grid");
  const { user: authUser } = useContext(AuthContext);
  const [action, setAction] = useState<UserAction>("Create");
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const { customerId } = useParams();
  const { isLoading, data } = useUsers(customerId);
  const users = data?.users ?? [];
  const createUserMutation = useCreateUser(customerId!);
  const editUserMutation = useEditUser(customerId!);
  const deleteUserMutation = useDeleteUser(customerId!);
  const showAlert = useAlert();
  const {
    isOpen: isPasswordResetOpen,
    setIsOpen: setIsPasswordResetOpen,
    handleOpen: openPasswordReset,
  } = useOpenable();

  const getFullName = (_: string, user: User) => {
    return `${user.firstName} ${user.lastName}`;
  };

  function resetPasswordTooltip(targetUser: User) {
    if (!isAdmin(authUser)) {
      return "Only admins can reset user's passwords.";
    }
    if (targetUser?.ssoUser) {
      return "Cannot reset password for SSO users.";
    }
    return "Reset Password";
  }

  const hasResetPasswordPermission =
    isAdmin(authUser) || authUser?.hasPermission("users:*");

  const columns: GridColDef<User>[] = [
    {
      field: "userId",
      headerName: t("grid:userId"),
    },
    {
      field: "fullName",
      headerName: t("grid:fullName"),
      valueGetter: getFullName,
      minWidth: 200,
      maxWidth: 400,
    },
    {
      field: "email",
      headerName: t("grid:email"),
      minWidth: 150,
      maxWidth: 400,
    },
    {
      field: "roleId",
      headerName: t("grid:role"),
      renderCell: (params) => (
        <RoleNameDisplay customerId={customerId} roleId={params.value} />
      ),
      minWidth: 150,
      maxWidth: 350,
    },
    {
      field: "authType",
      headerName: t("grid:authType"),
      valueGetter: (_, user) => (user.ssoUser ? "SSO" : "Standard"),
      minWidth: 100,
      maxWidth: 150,
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("grid:actions"),
      getActions: ({ row, id }) => [
        <Tooltip title={resetPasswordTooltip(row)}>
          <span>
            <GridActionsCellItem
              label={"Reset Password"}
              icon={<LockReset />}
              onClick={() => {
                setSelectedRowId(id as string);
                openPasswordReset();
              }}
              disabled={!hasResetPasswordPermission || row.ssoUser}
            />
          </span>
        </Tooltip>,
      ],
    },
  ];

  const getRowId = (user: User) => user.userId;

  return (
    <div className="h-full flex flex-col">
      {selectedRowId && (
        <AdminResetPasswordDialog
          isOpen={isPasswordResetOpen}
          setIsOpen={setIsPasswordResetOpen}
          userId={selectedRowId}
          customerId={customerId!}
        />
      )}
      <ResultsPage
        initialState={{
          columns: {
            columnVisibilityModel: {
              userId: false,
            },
          },
        }}
        permissionResource="users"
        tableId="users"
        entityName="User"
        columns={columns}
        rows={users}
        getRowId={getRowId}
        loading={isLoading}
        selectedRowId={selectedRowId}
        setSelectedRowId={setSelectedRowId}
        setCreateFormOpen={(open: boolean) => {
          setAction("Create");
          setIsFormOpen(open);
        }}
        setEditFormOpen={(open: boolean) => {
          setAction("Edit");
          setIsFormOpen(open);
        }}
        setDeleteDialogOpen={setIsDeleteDialogOpen}
      />
      <UserForm
        isOpen={isFormOpen}
        setIsOpen={setIsFormOpen}
        createUser={(formValues) =>
          createUser(
            formValues,
            createUserMutation,
            showAlert,
            setIsFormOpen,
          )
        }
        editUser={(formValues) =>
          editUser(
            formValues,
            selectedRowId,
            editUserMutation,
            showAlert,
            setIsFormOpen,
          )
        }
        action={action}
        selectedUser={
          users.find((user) => user.userId === selectedRowId) ?? null
        }
      />
      <DeleteDialog
        entityName="User"
        deleteEntity={() =>
          deleteUser(
            deleteUserMutation,
            selectedRowId,
            showAlert,
            users,
          )
        }
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
      />
    </div>
  );
}
